import React, { useEffect, useMemo, useState, useRef } from 'react';
import { updateCompanyExternalUsersFilter } from '../../data/stores/companyExternalUser';
import usePagination from '../../hooks/usePaginationStore';
import {
	fetchCompanyExternalUsers,
	resendExternalUserInvite,
	updateCompanyExternalUser,
} from '../../data/thunks/companyExternalThunk';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../data/store';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import ArchiveIconOutlined from '@mui/icons-material/ArchiveOutlined';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import {
	CompanyExternalUser,
	CompanyExternalUserStatuses,
} from '../../data/models/companyExternalUser';
import Table from '../common/Table';
import { GridColDef } from '@mui/x-data-grid';
import { Button } from '@mui/material';
import Avatar from '../common/Avatar';
import ToggleButtonComponent from '../common/buttons/ToggleButton';
import { styled } from 'styled-components';
import SearchBar from '../common/SearchBar';
import Menu from '../common/Menu';
import GroupsCell from '../common/GroupsCell';
import EditExternalCrewModal from './EditExternalCrewModal';
import ViewExternalCrewModal from './ViewExternalCrewModal';
import ConvertCrewModal from './ConvertCrewModal';
import ResendInvitationConfirmationModal from './ResendInvitationConfirmationModal';
import ArchiveExternalUserConfirmationModal from './ArchiveExternalUserConfirmationModal';
import UnarchiveExternalUserConfirmationModal from './UnarchiveExternalUserConfirmationModal';
import { convertExternalUser } from '../../data/thunks/userThunk';
import { ConvertUserDTO, CrewRoles } from '../../data/models/user';
import { getCompanyExternalUser } from '../../services/company-external-user.service';

const STATUS_FILTER_OPTIONS = [
	{ value: 'ALL', display: 'ALL' },
	{ value: 'ACCEPTED', display: 'ACCEPTED' },
	{ value: 'INVITED', display: 'INVITED' },
	{ value: 'NOT_INVITED', display: 'NOT INVITED' },
	{ value: 'ARCHIVED', display: 'ARCHIVED' },
];

export default function ExternalCrew() {
	const dispatch: AppDispatch = useDispatch();

	const { companyExternalUsers } = useSelector(
		(state: RootState) => state.companyExternalUserState
	);
	const { company } = useSelector((state: RootState) => state.companyState);

	const [selectedFilter, setSelectedFilter] = useState('ALL');
	const [isLoading, setIsLoading] = useState(false);
	const [isSearchLoading, setIsSearchLoading] = useState(false);
	const isLoadingRef = useRef(false);
	const [openEditModal, setOpenEditModal] = useState(false);
	const [openViewModal, setOpenViewModal] = useState(false);
	const [openConvertModal, setOpenConvertModal] = useState(false);
	const [openResendConfirmModal, setOpenResendConfirmModal] = useState(false);
	const [openArchiveConfirmModal, setOpenArchiveConfirmModal] =
		useState(false);
	const [openUnarchiveConfirmModal, setOpenUnarchiveConfirmModal] =
		useState(false);
	const [openBulkResendConfirmModal, setOpenBulkResendConfirmModal] =
		useState(false);
	const [openBulkArchiveConfirmModal, setOpenBulkArchiveConfirmModal] =
		useState(false);
	const [isInitialInvitation, setIsInitialInvitation] = useState(false);
	const [selectedUser, setSelectedUser] = useState<
		CompanyExternalUser | undefined
	>(undefined);
	const [selectedRows, setSelectedRows] = useState<string[]>([]);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState<
		Record<string, boolean>
	>({});

	const {
		searchText,
		setSearchText,
		page,
		setPage,
		pageSize,
		sortModel,
		setSortModel,
	} = usePagination(
		'companyExternalUserState',
		'companyExternalUsers',
		updateCompanyExternalUsersFilter
	);

	useEffect(() => {
		if (company?.id) {
			const filter = {
				companyId: company?.id,
				searchText,
				order: sortModel[0]?.field,
				direction: sortModel[0]?.sort,
				page: page,
				take: pageSize,
				include: {
					groups: true,
				},
				...(selectedFilter !== 'ALL' && { status: selectedFilter }),
			};
			dispatch(fetchCompanyExternalUsers(filter));
		}
	}, [company?.id]);

	const handleTextSearch = async (newSearchText: string) => {
		setSearchText(newSearchText);
		setPage(0);
		const filter = {
			companyId: company?.id,
			searchText: newSearchText,
			order:
				(sortModel && sortModel.length > 0 && sortModel[0]?.field) ||
				'created',
			direction:
				(sortModel && sortModel.length > 0 && sortModel[0]?.sort) ||
				'desc',
			page: 0,
			take: pageSize,
			include: {
				groups: true,
			},
			...(selectedFilter !== 'ALL' && { status: selectedFilter }),
		};
		setIsSearchLoading(true);
		try {
			await dispatch(fetchCompanyExternalUsers(filter));
		} finally {
			setIsSearchLoading(false);
		}
	};

	const handleFilterChange = (newFilterValue: string) => {
		setSelectedFilter(newFilterValue);
		setPage(0);
		const filter = {
			companyId: company?.id,
			searchText: searchText,
			order:
				(sortModel && sortModel.length > 0 && sortModel[0]?.field) ||
				'created',
			direction:
				(sortModel && sortModel.length > 0 && sortModel[0]?.sort) ||
				'desc',
			page: 0,
			take: pageSize,
			include: {
				groups: true,
			},
			...(newFilterValue !== 'ALL' && { status: newFilterValue }),
		};
		dispatch(fetchCompanyExternalUsers(filter));
	};

	const handleSortOnChange = (order) => {
		setSortModel(order);
		const filter = {
			companyId: company?.id,
			searchText: searchText,
			order: order[0]?.field,
			direction: order[0]?.sort,
			page: page,
			take: pageSize,
			include: {
				groups: true,
			},
			...(selectedFilter !== 'ALL' && { status: selectedFilter }),
		};
		dispatch(fetchCompanyExternalUsers(filter));
	};

	const handleOnPageChange = (newPage: number, scrollToTop = false) => {
		const filter = {
			companyId: company?.id,
			searchText: searchText,
			order: sortModel[0]?.field,
			direction: sortModel[0]?.sort,
			page: newPage,
			take: pageSize,
			include: {
				groups: true,
			},
			...(selectedFilter !== 'ALL' && { status: selectedFilter }),
		};
		setPage(newPage);
		dispatch(fetchCompanyExternalUsers(filter));
		if (scrollToTop) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	};

	const handleColumnVisibilityModelChange = (
		newModel: Record<string, boolean>
	) => {
		setColumnVisibilityModel(newModel);
	};

	const handleEdit = async (user: CompanyExternalUser) => {
		try {
			setIsLoading(true);
			const userToEdit = await getCompanyExternalUser(user.id);
			setSelectedUser(userToEdit);
			setOpenEditModal(true);
		} catch (error) {
			console.error('Error fetching user:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleView = (user: CompanyExternalUser) => {
		setSelectedUser(user);
		setOpenViewModal(true);
	};

	const handleArchive = (user: CompanyExternalUser) => {
		setSelectedUser(user);
		setOpenArchiveConfirmModal(true);
	};

	const handleUnarchive = (user: CompanyExternalUser) => {
		setSelectedUser(user);
		setOpenUnarchiveConfirmModal(true);
	};

	const handleConfirmArchive = async () => {
		if (!selectedUser) return;

		try {
			await dispatch(
				updateCompanyExternalUser(selectedUser.id, {
					status: 'ARCHIVED',
				})
			);
			// Refresh the list to get updated status
			const filter = {
				companyId: company?.id,
				searchText: searchText,
				order: sortModel[0]?.field,
				direction: sortModel[0]?.sort,
				page: page,
				take: pageSize,
				include: {
					groups: true,
				},
				...(selectedFilter !== 'ALL' && { status: selectedFilter }),
			};
			dispatch(fetchCompanyExternalUsers(filter));
		} catch (error) {
			console.error('Error archiving user:', error);
		}
	};

	const handleConfirmUnarchive = async () => {
		if (!selectedUser) return;

		try {
			await dispatch(
				updateCompanyExternalUser(selectedUser.id, { status: 'ACTIVE' })
			);
			// Refresh the list to get updated status
			const filter = {
				companyId: company?.id,
				searchText: searchText,
				order: sortModel[0]?.field,
				direction: sortModel[0]?.sort,
				page: page,
				take: pageSize,
				include: {
					groups: true,
				},
				...(selectedFilter !== 'ALL' && { status: selectedFilter }),
			};
			dispatch(fetchCompanyExternalUsers(filter));
		} catch (error) {
			console.error('Error unarchiving user:', error);
		}
	};

	const handleSendInvitation = (user: CompanyExternalUser) => {
		setSelectedUser(user);
		setIsInitialInvitation(true);
		setOpenResendConfirmModal(true);
	};

	const handleResendInvitation = (user: CompanyExternalUser) => {
		setSelectedUser(user);
		setIsInitialInvitation(false);
		setOpenResendConfirmModal(true);
	};

	const handleConfirmInvitation = async (customMessage?: string) => {
		if (!selectedUser) return;

		try {
			setIsLoading(true);
			await dispatch(
				resendExternalUserInvite(selectedUser.id, customMessage)
			);
			// Refresh the list to get updated status
			const filter = {
				companyId: company?.id,
				searchText: searchText,
				order: sortModel[0]?.field,
				direction: sortModel[0]?.sort,
				page: page,
				take: pageSize,
				include: {
					groups: true,
				},
				...(selectedFilter !== 'ALL' && { status: selectedFilter }),
			};
			dispatch(fetchCompanyExternalUsers(filter));
		} catch (error) {
			console.error('Error sending invitation:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleBulkResendInvitations = () => {
		if (selectedRows.length === 0) return;
		setOpenBulkResendConfirmModal(true);
	};

	const handleBulkArchive = () => {
		if (selectedRows.length === 0) return;
		setOpenBulkArchiveConfirmModal(true);
	};

	const handleConfirmBulkResendInvitations = async (
		customMessage?: string
	) => {
		if (selectedRows.length === 0) return;
		setIsLoading(true);
		try {
			// Send invitations for all selected users
			const promises = selectedRows.map((userId) =>
				dispatch(resendExternalUserInvite(userId, customMessage))
			);
			await Promise.all(promises);

			// Clear selection and refresh the list
			setSelectedRows([]);
			const filter = {
				companyId: company?.id,
				searchText: searchText,
				order: sortModel[0]?.field,
				direction: sortModel[0]?.sort,
				page: page,
				take: pageSize,
				include: {
					groups: true,
				},
				...(selectedFilter !== 'ALL' && { status: selectedFilter }),
			};
			dispatch(fetchCompanyExternalUsers(filter));
		} catch (error) {
			console.error('Error sending bulk invitations:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleConfirmBulkArchive = async () => {
		if (selectedRows.length === 0) return;

		try {
			// Archive all selected users
			const promises = selectedRows.map((userId) =>
				dispatch(
					updateCompanyExternalUser(userId, { status: 'ARCHIVED' })
				)
			);
			await Promise.all(promises);

			// Clear selection and refresh the list
			setSelectedRows([]);
			const filter = {
				companyId: company?.id,
				searchText: searchText,
				order: sortModel[0]?.field,
				direction: sortModel[0]?.sort,
				page: page,
				take: pageSize,
				include: {
					groups: true,
				},
				...(selectedFilter !== 'ALL' && { status: selectedFilter }),
			};
			dispatch(fetchCompanyExternalUsers(filter));
		} catch (error) {
			console.error('Error archiving users:', error);
		}
	};

	const handleConvert = async (userToCreate: ConvertUserDTO) => {
		await dispatch(convertExternalUser(userToCreate));
		const filter = {
			companyId: company?.id,
			searchText: searchText,
			order: sortModel[0]?.field,
			direction: sortModel[0]?.sort,
			page: page,
			take: pageSize,
			include: {
				groups: true,
			},
			...(selectedFilter !== 'ALL' && { status: selectedFilter }),
		};
		await dispatch(fetchCompanyExternalUsers(filter));

		// Add a small delay to allow DOM to settle before closing modals
		// This prevents ResizeObserver errors
		setTimeout(() => {
			setOpenEditModal(false);
			setOpenConvertModal(false);
		}, 0);
	};

	const getMenuItems = (user: CompanyExternalUser) => {
		const menuItems: any[] = [];
		const status = user.status?.toUpperCase();

		// Edit option - always available
		menuItems.push({
			display: 'Edit',
			onClick: () => handleEdit(user),
			icon: <EditIcon sx={{ mr: 1 }} />,
		});

		// Add status-specific action menu items
		switch (status) {
			case CompanyExternalUserStatuses.INVITE_EXPIRED.id:
				menuItems.push({
					display: 'Resend Invitation',
					onClick: () => handleResendInvitation(user),
					icon: <SendIcon sx={{ mr: 1 }} />,
				});
				menuItems.push({
					display: 'Archive',
					onClick: () => handleArchive(user),
					icon: <ArchiveIconOutlined sx={{ mr: 1 }} />,
				});
				break;

			case CompanyExternalUserStatuses.NOT_INVITED.id:
				menuItems.push({
					display: 'Send Invitation',
					onClick: () => handleSendInvitation(user),
					icon: <SendIcon sx={{ mr: 1 }} />,
				});
				menuItems.push({
					display: 'Archive',
					onClick: () => handleArchive(user),
					icon: <ArchiveIconOutlined sx={{ mr: 1 }} />,
				});
				break;

			case CompanyExternalUserStatuses.ARCHIVED.id:
				menuItems.push({
					display: 'Un-archive',
					onClick: () => handleUnarchive(user),
					icon: <UnarchiveIcon sx={{ mr: 1 }} />,
				});
				break;

			case CompanyExternalUserStatuses.ACTIVE.id:
			case CompanyExternalUserStatuses.INVITED.id:
			default:
				menuItems.push({
					display: 'Resend Invitation',
					onClick: () => handleResendInvitation(user),
					icon: <SendIcon sx={{ mr: 1 }} />,
				});
				menuItems.push({
					display: 'Archive',
					onClick: () => handleArchive(user),
					icon: <ArchiveIconOutlined sx={{ mr: 1 }} />,
				});
				break;
		}

		return menuItems;
	};

	const renderActionsMenu = (user: CompanyExternalUser) => {
		return <Menu menuItems={getMenuItems(user)} iconButton={true} />;
	};

	const renderBulkActions = () => {
		const hasSelection = selectedRows.length > 0;

		if (!hasSelection) return <></>;

		return (
			<BulkActionsButtonContainer>
				<Button
					variant="outlined"
					size="small"
					startIcon={<SendIcon />}
					onClick={handleBulkResendInvitations}
					disabled={!hasSelection}
					sx={{ marginRight: 1 }}
				>
					Request Information ({selectedRows.length})
				</Button>
			</BulkActionsButtonContainer>
		);
	};

	const columns: GridColDef[] = useMemo(() => {
		return [
			{
				field: 'name',
				headerName: 'Name',
				minWidth: 200,
				disableColumnMenu: true,
				renderCell: (params) => {
					const user = params.row as CompanyExternalUser;
					const profilePictureUrl =
						user.userProfile?.profilePictureUrl?.publicUrl;
					const initials = `${user.firstName?.charAt(0) || ''}${
						user.lastName?.charAt(0) || ''
					}`.toUpperCase();

					return (
						// <NameCell onClick={() => handleView(params.row)}>
						<NameCell>
							<Avatar
								src={profilePictureUrl}
								sx={{ width: 32, height: 32 }}
								alt={`${user.firstName} ${user.lastName}`}
							>
								{initials || user.firstName?.charAt(0) || '?'}
							</Avatar>
							{/* <ClickableName>{params.value}</ClickableName> */}
							<div>{params.value}</div>
						</NameCell>
					);
				},
			},
			{
				field: 'role',
				headerName: 'Role',
				minWidth: 150,
				disableColumnMenu: true,
				renderCell: (params) => (
					<div>
						{CrewRoles[params.value]?.display || params.value}
					</div>
				),
			},
			{
				field: 'phone',
				headerName: 'Phone Number',
				minWidth: 150,
				disableColumnMenu: true,
				renderCell: (params) =>
					params.value ? (
						<PhoneLink href={`tel:${params.value}`}>
							{params.value}
						</PhoneLink>
					) : (
						'-'
					),
			},
			{
				field: 'email',
				headerName: 'Email',
				minWidth: 250,
				disableColumnMenu: true,
				renderCell: (params) => (
					<EmailLink href={`mailto:${params.value}`}>
						{params.value}
					</EmailLink>
				),
			},
			{
				field: 'status',
				headerName: 'Status',
				minWidth: 150,
				disableColumnMenu: true,
				renderCell: (params) => <StatusCell status={params.value} />,
			},
			{
				field: 'groups',
				headerName: 'Groups',
				minWidth: 270,
				disableColumnMenu: true,
				renderCell: (params) => <GroupsCell groups={params.value} />,
			},
			{
				field: 'actions',
				headerName: '',
				width: 140,
				disableColumnMenu: true,
				align: 'center',
				headerAlign: 'center',
				renderCell: (params) => renderActionsMenu(params.row),
			},
		];
	}, []);

	const rows = useMemo(() => {
		if (!companyExternalUsers || !companyExternalUsers.list) {
			return [];
		}
		return companyExternalUsers.list.map((user) => ({
			...user,
			name: `${user.firstName} ${user.lastName}`,
			role: user.role || '-',
		}));
	}, [companyExternalUsers.list]);

	return (
		<div>
			<EditExternalCrewModal
				open={openEditModal}
				handleClose={() => setOpenEditModal(false)}
				user={selectedUser || undefined}
				handleConvert={() => setOpenConvertModal(true)}
			/>
			<ViewExternalCrewModal
				open={openViewModal}
				handleClose={() => setOpenViewModal(false)}
				user={selectedUser || undefined}
			/>
			<ConvertCrewModal
				open={openConvertModal}
				handleClose={() => setOpenConvertModal(false)}
				user={selectedUser || undefined}
				handleSave={handleConvert}
			/>
			<ResendInvitationConfirmationModal
				open={openResendConfirmModal}
				handleClose={() => setOpenResendConfirmModal(false)}
				user={selectedUser}
				onConfirm={handleConfirmInvitation}
				isInitialInvitation={isInitialInvitation}
			/>
			<ArchiveExternalUserConfirmationModal
				open={openArchiveConfirmModal}
				handleClose={() => setOpenArchiveConfirmModal(false)}
				user={selectedUser}
				onConfirm={handleConfirmArchive}
			/>
			<UnarchiveExternalUserConfirmationModal
				open={openUnarchiveConfirmModal}
				handleClose={() => setOpenUnarchiveConfirmModal(false)}
				user={selectedUser}
				onConfirm={handleConfirmUnarchive}
			/>
			<ResendInvitationConfirmationModal
				open={openBulkResendConfirmModal}
				handleClose={() => setOpenBulkResendConfirmModal(false)}
				users={
					selectedRows
						.map((id) =>
							companyExternalUsers?.list?.find(
								(user) => user.id === id
							)
						)
						.filter(Boolean) as CompanyExternalUser[]
				}
				onConfirm={handleConfirmBulkResendInvitations}
				isInitialInvitation={false}
			/>
			<ArchiveExternalUserConfirmationModal
				open={openBulkArchiveConfirmModal}
				handleClose={() => setOpenBulkArchiveConfirmModal(false)}
				users={
					selectedRows
						.map((id) =>
							companyExternalUsers?.list?.find(
								(user) => user.id === id
							)
						)
						.filter(Boolean) as CompanyExternalUser[]
				}
				onConfirm={handleConfirmBulkArchive}
			/>

			{/* Header with filters and search */}
			<HeaderContainer>
				<FiltersContainer>
					<ToggleButtonComponent
						value={selectedFilter}
						onChange={handleFilterChange}
						options={STATUS_FILTER_OPTIONS}
					/>
				</FiltersContainer>

				<SearchContainer>
					<SearchBar
						searchText={searchText}
						handleTextSearch={handleTextSearch}
						setSearchText={setSearchText}
						handleClear={() => handleTextSearch('')}
					/>
				</SearchContainer>
			</HeaderContainer>

			<Table
				columns={columns}
				rows={rows}
				page={page}
				pageSize={pageSize}
				rowCount={companyExternalUsers?.count || 0}
				sortModel={sortModel}
				onSortModelChange={handleSortOnChange}
				onPaginationModelChange={handleOnPageChange}
				loading={
					!companyExternalUsers?.initialized ||
					isLoading ||
					isSearchLoading
				}
				checkboxSelection={true}
				selectionModel={selectedRows}
				onSelectionModelChange={setSelectedRows}
				bulkActions={renderBulkActions()}
				columnVisibilityModel={columnVisibilityModel}
				onColumnVisibilityModelChange={
					handleColumnVisibilityModelChange
				}
				enableColumnVisibilityToggle={true}
				disableRowSelectionOnClick={true}
				noRowsContent={'Click the + New button to add a Crew Member'}
			/>
		</div>
	);
}

const StatusCell = ({ status }: { status: string }) => {
	const getStatusColor = (status: string) => {
		switch (status?.toUpperCase()) {
			case 'ACCEPTED':
				return 'primary';
			case 'INVITED':
				return 'success';
			case 'INVITATION_EXPIRED':
			case 'EXPIRED':
				return 'warning';
			case 'NOT_INVITED':
				return 'default';
			case 'ARCHIVED':
				return 'error';
			default:
				return 'default';
		}
	};

	const getStatusLabel = (status: string) => {
		switch (status?.toUpperCase()) {
			case 'ACCEPTED':
				return 'Accepted';
			case 'INVITED':
				return 'Invited';
			case 'INVITATION_EXPIRED':
				return 'Invitation Expired';
			case 'NOT_INVITED':
				return 'Not Invited';
			case 'EXPIRED':
				return 'Expired';
			case 'ARCHIVED':
				return 'Archived';
			default:
				return status || 'Unknown';
		}
	};

	return <StatusChip $status={status}>{getStatusLabel(status)}</StatusChip>;
};

const HeaderContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-top: 16px;
	margin-bottom: 16px;
	gap: 16px;
`;

const FiltersContainer = styled.div`
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
`;

const SearchContainer = styled.div`
	display: flex;
	gap: 12px;
	align-items: center;
`;

const NameCell = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	cursor: pointer;
	&:hover {
		opacity: 0.8;
	}
`;

const ClickableName = styled.span`
	color: ${({ theme }) => theme.palette.primary.main};
	text-decoration: none;
	&:hover {
		text-decoration: underline;
	}
`;

const PhoneLink = styled.a`
	color: ${({ theme }) => theme.palette.primary.main};
	text-decoration: none;
	&:hover {
		text-decoration: underline;
	}
`;

const EmailLink = styled.a`
	color: ${({ theme }) => theme.palette.primary.main};
	text-decoration: none;
	&:hover {
		text-decoration: underline;
	}
`;

const StatusChip = styled.div<{ $status: string }>`
	color: ${({ theme, $status }) => {
		switch ($status?.toUpperCase()) {
			case 'ACCEPTED':
				return theme.palette.primary.main;
			case 'INVITED':
				return theme.palette.success.main;
			case 'INVITATION_EXPIRED':
			case 'EXPIRED':
				return theme.palette.warning.main;
			case 'ARCHIVED':
				return theme.palette.error.main;
			default:
				return theme.palette.text.secondary;
		}
	}};
	font-weight: 500;
`;

const BulkActionsButtonContainer = styled.div`
	display: flex;
	gap: 8px;
	align-items: center;
`;
